import { Paper } from "@mui/material";
import { alpha } from "@mui/material";
import { COLOR_BLACK, COLOR_GREY_BORDER, COLOR_SECONDARY } from "helpers/colors";
import { FONT_FAMILY_GRAPHIK, FONT_FAMILY_KNOCKOUT } from "helpers/FONT";
import { SPACING, SPACING_DENSE, SPACING_HALF } from "helpers/spacings";
import React from "react";

const STAT_ABBR = {
  Points: "ppg",
  Rebounds: "rpg",
  Assists: "apg",
  Steals: "spg",
  Blocks: "bpg",
};

const cardTitleStyle = {
  fontFamily: FONT_FAMILY_KNOCKOUT,
  fontWeight: 400,
  fontSize: 15,
  textTransform: "uppercase",
  letterSpacing: 0.5,
  color: COLOR_BLACK,
  marginBottom: SPACING,
  paddingBottom: SPACING_HALF,
  borderBottom: `1px solid ${alpha(COLOR_BLACK, 0.08)}`,
};

export default function TeamPerformance({ rows = [] }) {
  if (!rows.length) return null;

  const maxAvg = Math.max(...rows.map((r) => r.avgCell?.value ?? 0));

  return (
    <div style={{ display: "flex", flexFlow: "column nowrap", gap: SPACING }}>
      {/* Team Averages */}
      <Paper style={{ borderRadius: 12, padding: SPACING }}>
        <div style={cardTitleStyle}>Team Averages</div>

        {rows.map((row) => {
          const avg = row.avgCell?.value ?? 0;
          const pct = maxAvg > 0 ? (avg / maxAvg) * 100 : 0;
          const leader = row.avgCell?.people?.[0]?.fullNameWithAnonymisation;
          const abbr = STAT_ABBR[row.title] || "pg";
          const total = row.totalCell?.value;

          return (
            <div key={row.title} style={{ marginBottom: SPACING }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
                <div
                  style={{
                    fontFamily: FONT_FAMILY_KNOCKOUT,
                    fontSize: 13,
                    letterSpacing: 0.8,
                    textTransform: "uppercase",
                    fontWeight: 400,
                    color: COLOR_BLACK,
                  }}
                >
                  {row.title}
                </div>
                <div
                  style={{
                    fontFamily: FONT_FAMILY_GRAPHIK,
                    fontWeight: 600,
                    fontSize: 20,
                    lineHeight: 1,
                    color: COLOR_BLACK,
                  }}
                >
                  {avg}
                </div>
              </div>

              <div
                style={{
                  height: 20,
                  backgroundColor: alpha(COLOR_BLACK, 0.08),
                  borderRadius: 6,
                  margin: `${SPACING_DENSE} 0`,
                }}
              >
                <div
                  style={{
                    width: `${pct}%`,
                    height: "100%",
                    backgroundColor: COLOR_SECONDARY,
                    borderRadius: 6,
                    transition: "width 0.4s ease",
                  }}
                />
              </div>

              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  fontSize: 12,
                  fontFamily: FONT_FAMILY_GRAPHIK,
                }}
              >
                {leader ? (
                  <div style={{ color: alpha(COLOR_BLACK, 0.65) }}>
                    {"Led by "}
                    <strong>{leader}</strong>
                    {` (${avg} ${abbr})`}
                  </div>
                ) : (
                  <div />
                )}
                {total != null && <div style={{ color: alpha(COLOR_BLACK, 0.4) }}>{total} total</div>}
              </div>
            </div>
          );
        })}
      </Paper>

      {/* Game Highs */}
      <Paper style={{ borderRadius: 12, padding: SPACING }}>
        <div style={cardTitleStyle}>Game Highs</div>

        <div style={{ display: "flex", flexFlow: "row nowrap" }}>
          {rows.map((row, i) => {
            const high = row.highCell?.value;
            const person = row.highCell?.people?.[0]?.fullNameWithAnonymisation;
            const abbr = STAT_ABBR[row.title]?.toUpperCase();

            return (
              <div
                key={row.title}
                style={{
                  flex: "1 1 0",
                  textAlign: "center",
                  padding: `${SPACING_HALF} ${SPACING_DENSE}`,
                  borderLeft: i > 0 ? `1px solid ${COLOR_GREY_BORDER}` : "none",
                }}
              >
                <div
                  style={{
                    fontFamily: FONT_FAMILY_GRAPHIK,
                    fontWeight: 600,
                    fontSize: 32,
                    lineHeight: 1,
                    color: COLOR_BLACK,
                  }}
                >
                  {high ?? "—"}
                </div>
                <div
                  style={{
                    fontFamily: FONT_FAMILY_GRAPHIK,
                    fontSize: 10,
                    color: alpha(COLOR_BLACK, 0.4),
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                    marginTop: 6,
                  }}
                >
                  {abbr}
                </div>
                {person && (
                  <div
                    style={{
                      fontFamily: FONT_FAMILY_GRAPHIK,
                      fontSize: 11,
                      color: alpha(COLOR_BLACK, 0.6),
                      marginTop: 4,
                    }}
                  >
                    {person}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </Paper>
    </div>
  );
}
